import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { Zap, Menu, X, Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { fetchPlatformSettings, applyBrandingToDocument } from '../../services/platformSettings';
import './PublicLayout.css';

const FALLBACK_PARTNERS = ['AWS', 'Microsoft Azure', 'Google Cloud', 'GitHub', 'Netlify', 'Vercel', 'MongoDB', 'PostgreSQL', 'Docker', 'Kubernetes'];

export default function PublicLayout() {
  const { data } = useApp();
  const { pathname, hash } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [branding, setBranding] = useState(null);

  const partnerChips = useMemo(() => {
    const list = (data.websitePartners || []).map(p => p.name).filter(Boolean);
    const base = list.length ? list : FALLBACK_PARTNERS;
    return [...base, ...base];
  }, [data.websitePartners]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchPlatformSettings().then((s) => {
      if (!cancelled) setBranding(s.branding);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const onUpdate = (e) => {
      const next = e?.detail?.branding;
      if (!next) return;
      setBranding(next);
      applyBrandingToDocument(next);
    };
    window.addEventListener('fortitude:platformSettingsUpdated', onUpdate);
    return () => window.removeEventListener('fortitude:platformSettingsUpdated', onUpdate);
  }, []);

  return (
    <div className="public-layout">
      {/* Navbar */}
      <nav className={`public-nav ${scrolled ? 'scrolled' : ''}`}>
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <div className="nav-logo-icon">
              {branding?.logoUrl ? <img src={branding.logoUrl} alt="" className="system-logo-img" /> : <Zap size={20} />}
            </div>
            <div>
              <span className="nav-brand">{branding?.companyName?.toUpperCase() || 'FORTITUDE'}</span>
              <span className="nav-brand-sub">IT Solutions</span>
            </div>
          </Link>

          <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
            {[
              { to: '/', label: 'Home', end: true },
              { to: '/services', label: 'Services' },
              { to: '/portfolio', label: 'Portfolio' },
              { to: '/#fortitude-news', label: 'News', isNews: true },
              { to: '/careers', label: 'Careers' },
              { to: '/contact', label: 'Contact' },
            ].map(({ to, label, end, isNews }) => {
              if (isNews) {
                return (
                  <Link
                    key={to}
                    to={to}
                    className={`nav-link ${pathname === '/' && hash === '#fortitude-news' ? 'active' : ''}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {label}
                  </Link>
                );
              }
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={!!end}
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  onClick={() => setMenuOpen(false)}
                >
                  {label}
                </NavLink>
              );
            })}
            <Link to="/login" className="btn-gold nav-cta" onClick={() => setMenuOpen(false)}>
              Client Portal
            </Link>
          </div>

          <button className="nav-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Page content */}
      <main className="public-main">
        <Outlet />
      </main>

      {/* Partners Carousel */}
      <section className="partners-section">
        <p className="partners-label">Trusted Technology Partners</p>
        <div className="partners-track-wrap">
          <div className="partners-track">
            {partnerChips.map((p, i) => (
              <div key={`${p}-${i}`} className="partner-chip">{p}</div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="public-footer">
        <div className="footer-container">
          <div className="footer-top">
            <div className="footer-brand">
              <div className="nav-logo" style={{ marginBottom: 16 }}>
                <div className="nav-logo-icon">{branding?.logoUrl ? <img src={branding.logoUrl} alt="" className="system-logo-img" /> : <Zap size={18} />}</div>
                <div>
                  <span className="nav-brand" style={{ fontSize: '1.1rem' }}>{branding?.companyName?.toUpperCase() || 'FORTITUDE'}</span>
                  <span className="nav-brand-sub">IT Solutions</span>
                </div>
              </div>
              <p className="footer-tagline">{branding?.tagline || 'Building digital solutions that drive growth and innovation across Africa.'}</p>
              <div className="footer-socials">
                {[Twitter, Linkedin, Facebook, Instagram].map((Icon, i) => (
                  <a key={i} href="#" className="footer-social"><Icon size={16} /></a>
                ))}
              </div>
            </div>

            <div className="footer-links-group">
              <h4 className="footer-links-title">Services</h4>
              <a href="#" className="footer-link">Software Development</a>
              <a href="#" className="footer-link">Web Development</a>
              <a href="#" className="footer-link">System Maintenance</a>
              <a href="#" className="footer-link">Graphic Design</a>
            </div>

            <div className="footer-links-group">
              <h4 className="footer-links-title">Company</h4>
              <Link to="/portfolio" className="footer-link">Portfolio</Link>
              <Link to="/careers" className="footer-link">Careers</Link>
              <Link to="/contact" className="footer-link">Contact</Link>
              <Link to="/login" className="footer-link">Client Portal</Link>
            </div>

            <div className="footer-links-group">
              <h4 className="footer-links-title">Contact</h4>
              <div className="footer-contact-item"><Mail size={13} /> admin@fitss.co.za</div>
              <div className="footer-contact-item"><Phone size={13} /> +27 11 000 0000</div>
              <div className="footer-contact-item"><MapPin size={13} /> Sandton, Johannesburg</div>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="footer-copy">© {new Date().getFullYear()} Fortitude IT Solutions. All rights reserved.</p>
            <div className="footer-bottom-links">
              <a href="#" className="footer-link">Privacy Policy</a>
              <a href="#" className="footer-link">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
